/* eslint-disable camelcase, no-bitwise */
import { BadgeOutlined, EmailOutlined, MarkunreadMailboxOutlined, ThumbUpAltOutlined } from '@mui/icons-material';
import { Alert, Box, CircularProgress, Divider, Grid, Link, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { getMembers, getKivraReachable } from 'src/shared/services/memberService';
import { getInvoiceGroupById } from 'src/shared/services/invoiceGroupService';
import { MemberSearchFilter } from 'src/modules/member/models/memberSearchFilter';
import { MemberStatusEnum } from 'src/shared/models/enums/memberStatusEnum';
import { DeliveryMethodPicker, labelOf, KIVRA_HELP_URL } from 'src/modules/sending/components/deliveryMethodPicker';
import {
  CH_EMAIL,
  CH_KIVRA,
  CH_LETTER,
  CountryBreakdown,
  SelectionBreakdown,
  useSendingWizard,
} from 'src/modules/sending/contexts/sendingWizardContext';
import { computeSegments, CHANNEL_COLOR } from 'src/modules/sending/functions/deliveryReach';

// Hämtar upp till så här många rader för att räkna kanal-täckningen klientsidan. Över detta blir det uppskattat.
const BREAKDOWN_CAP = 20000;
// Urvalet baseras alltid på aktiva medlemmar: Betald + Ej betald (aldrig intresseanmälda/utträdda).
const BASE_STATUS = [MemberStatusEnum.Paid, MemberStatusEnum.NotPaid];

// Tomt land eller "Sverige" räknas som Sverige, övriga särskiljs.
const normCountry = (c?: string) => {
  const t = (c ?? '').trim();
  return t === '' || t.toLowerCase() === 'sverige' ? 'Sverige' : t;
};

export const SendingWizardStep4Delivery = () => {
  const isMounted = useIsMounted();
  const { setValue, watch } = useFormContext();
  const {
    coverage,
    setCoverage,
    dueDateFrom,
    dueDateTom,
    profileFilters,
    excludeProfileFilters,
    dueConfirmed,
    includeConfirmed,
    excludeConfirmed,
    cacheRef,
    led,
    reminderCountsVisible,
    reminderPreviousPayDate,
  } = useSendingWizard();
  const ownerId = watch('owner_id') as number | undefined; // faktureringsgrupp (för Kivra-kollen)
  // Samlingsfaktura (default Ja): hushållsmedlemmar slås ihop på huvudmedlemmens faktura → färre fakturor (mottagare).
  const householdInvoice = watch('householdInvoice') !== false;

  const [breakdown, setBreakdown] = useState<SelectionBreakdown | null>(null);
  // null = ännu ej kontrollerat, false = ingen Kivra-koppling (tenantKey saknas) → inget Kivra-anrop görs alls.
  const [kivraConfigured, setKivraConfigured] = useState<boolean | null>(null);

  const orderString = (watch('deliveryMethodOrder') as string | undefined) ?? '';
  const order = useMemo(() => (orderString ? orderString.split(',').filter((k) => k.length > 0) : []), [orderString]);

  // Är Kivra aktiverat för faktureringsgruppen? = gruppen har en kivraTenantKey. Läses direkt från faktureringsgruppen
  // (samma auktoritativa signal som det skarpa utskicket/mailet). Tidigare pollades en extern Kivra-tjänst, men den kan
  // svara med fel även när Kivra ÄR aktiverat → gav en falsk "Kivra inte aktiverat"-varning (och hoppade över nåbarheten).
  useEffect(() => {
    if (!ownerId || ownerId <= 0) {
      setKivraConfigured(false);
      return;
    }
    (async () => {
      try {
        const res = await getInvoiceGroupById(ownerId);
        if (isMounted()) setKivraConfigured(!!(res.data?.kivraTenantKey ?? '').trim());
      } catch (e) {
        if (isMounted()) setKivraConfigured(false);
      }
    })();
  }, [ownerId, isMounted]);

  // Kanal-täckning (masks) + landsnedbrytning: körs här (i Leveransmetod-steget) så det tunga medlems-anropet hinner
  // slutföras medan steget är monterat. Medlemmar-steget har redan satt antalen (coverage med tom masks) – vi fyller på.
  useEffect(() => {
    // Kör bara när antalen finns men masks ännu är tomma (annars redan beräknat), urvalet är bekräftat och Kivra-probad.
    // Guarden på masks.length gör att effekten INTE kör om igen efter att den själv satt masks (undviker loop).
    if (!coverage || coverage.masks.length > 0) return undefined;
    if (!(dueConfirmed && includeConfirmed && excludeConfirmed)) return undefined;
    if (kivraConfigured === null) return undefined;

    const filter: MemberSearchFilter = { status: [...BASE_STATUS] };
    if (dueDateFrom) filter.dueDateStart = dueDateFrom.format('YYYYMMDD');
    if (dueDateTom) filter.dueDateEnd = dueDateTom.format('YYYYMMDD');
    if (profileFilters.length) filter.profileItemIds = profileFilters.map((p) => p.itemId);
    if (excludeProfileFilters.length) filter.excludedProfileItemIds = excludeProfileFilters.map((p) => p.itemId);

    const handle = setTimeout(async () => {
      try {
        // Tungt anrop: hämta raderna och räkna kanal-täckning per kombination (e-post/Kivra/brev) + landsnedbrytning.
        const res = await getMembers(0, BREAKDOWN_CAP, 'member_id', 'asc', filter, [], '');
        if (!isMounted()) return;
        const rows = res.data.results ?? [];

        // Kivra-nåbarhet (server-koll mot faktureringsgruppen). En medlem är nåbar om e-post ELLER personnummer finns i Kivra.
        // Görs bara om faktureringsgruppen faktiskt har en Kivra-koppling (tenantKey) – annars är anropet onödigt.
        let allowedEmails: Set<string> | null = null;
        let allowedSsns: Set<string> | null = null;
        if (kivraConfigured && ownerId && ownerId > 0 && rows.length > 0) {
          try {
            const emails = rows.map((m) => m.email).filter((e): e is string => !!e);
            const ssns = rows.map((m) => m.birthDate).filter((s): s is string => !!s);
            const kres = await getKivraReachable(ownerId, emails, ssns);
            if (!isMounted()) return;
            allowedEmails = new Set((kres.data.emails ?? []).map((e) => e.toLowerCase()));
            allowedSsns = new Set(kres.data.ssNs ?? []);
          } catch (e) {
            ConsoleHelper.log(e);
          }
        }
        const allowedE = allowedEmails;
        const allowedS = allowedSsns;
        const kivraAvailable = allowedE != null && allowedS != null;

        // Mottagare (fakturor): vid samlingsfaktura får hushållsmedlemmar ingen egen faktura (slås ihop på
        // huvudmedlemmens), så mottagarna = alla UTOM hushållsmedlemmar (isFamilyMember). Kanal-täckning räknas på dessa.
        const recipientRows = householdInvoice ? rows.filter((m) => !m.isFamilyMember) : rows;

        const byCountry = new Map<string, CountryBreakdown>();
        const masks = new Array<number>(8).fill(0);
        recipientRows.forEach((m) => {
          const hasEmail = (m.email ?? '').includes('@');
          const hasPnr = (m.birthDate ?? '').replace(/\D/g, '').length >= 10;
          const hasPost = (m.postNr ?? '').trim() !== '';
          const kivraReach =
            !!allowedE && !!allowedS && ((!!m.email && allowedE.has(m.email.toLowerCase())) || (!!m.birthDate && allowedS.has(m.birthDate)));

          const country = normCountry(m.country);
          const g = byCountry.get(country) ?? { country, count: 0, email: 0, pnr: 0, post: 0 };
          g.count += 1;
          if (hasEmail) g.email += 1;
          if (hasPnr) g.pnr += 1;
          if (hasPost) g.post += 1;
          byCountry.set(country, g);

          let bit = 0;
          if (hasEmail) bit |= CH_EMAIL;
          if (kivraReach) bit |= CH_KIVRA;
          if (hasPost) bit |= CH_LETTER;
          masks[bit] += 1;
        });

        // Skala upp om vi nådde taket (BREAKDOWN_CAP): membersCovered är det exakta antalet från antalsfrågan.
        const approx = coverage.membersCovered > rows.length;
        const scale = approx && rows.length > 0 ? coverage.membersCovered / rows.length : 1;
        const countries = Array.from(byCountry.values())
          .map((g) => ({
            country: g.country,
            count: Math.round(g.count * scale),
            email: Math.round(g.email * scale),
            pnr: Math.round(g.pnr * scale),
            post: Math.round(g.post * scale),
          }))
          .sort((a, b) => {
            if (a.country === 'Sverige') return -1;
            if (b.country === 'Sverige') return 1;
            return b.count - a.count;
          });

        const bd: SelectionBreakdown = { approx, countries };
        const cov = { ...coverage, masks: masks.map((v) => Math.round(v * scale)), approx, kivraAvailable };
        setBreakdown(bd);
        setCoverage(cov);
        // Uppdatera cachen så täckningen finns kvar vid stegnavigering (utan nytt tungt anrop).
        if (cacheRef.current) cacheRef.current = { ...cacheRef.current, breakdown: bd, coverage: cov };
      } catch (e) {
        if (isMounted()) ConsoleHelper.log(e);
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [
    coverage,
    dueDateFrom,
    dueDateTom,
    profileFilters,
    excludeProfileFilters,
    dueConfirmed,
    includeConfirmed,
    excludeConfirmed,
    kivraConfigured,
    ownerId,
    householdInvoice,
    isMounted,
    setCoverage,
    cacheRef,
  ]);

  // Antal nåbara medlemmar räknas via den delade reach-hjälpen (samma logik som sammanfattningssteget).
  // För en påminnelse gäller inte huvudutskickets täckningssiffror, och "obetalda" säger inget förrän fakturorna
  // förfallit – då döljs antalen helt i det här steget.
  const hideCounts = led !== 0 && !reminderCountsVisible;

  const pre = coverage?.approx ? '≈ ' : '';
  // Kanal-täckningen (masks) fylls i av det tunga medlems-anropet EFTER att antalen räknats. Tills det är klart är
  // masks tom – då visar vi "beräknas" i stället för 0, så det inte ser ut som att ingen kan nås.
  const channelsLoading = coverage != null && coverage.masks.length === 0;

  // Segment för progressbaren: varje vald metod bidrar med hur många NYA medlemmar den når (PDF når alla återstående).
  const segments = computeSegments(order, coverage);
  const reachedTotal = segments.reduce((sum, s) => sum + s.inc, 0);
  const notReached = coverage ? Math.max(0, coverage.total - reachedTotal) : 0;
  // Röd varning visas först när kedjan är "klar" – alla tre metoder valda eller när alla redan nåtts.
  const finalized = order.length === 3 || (coverage != null && order.length > 0 && notReached === 0);

  return (
    <GridWrapper>
      <Grid item xs={12} md={7}>
        {/* Samma vänsterindrag som korten (tidslinjens gutter) så rubriken linjerar med kortens innehåll. */}
        <Box sx={{ mb: 2, pl: { xs: 6, sm: 7 } }}>
          <Typography variant='h5' sx={{ fontWeight: 700, mb: 0.5 }}>
            Hur ska fakturorna skickas ut?
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            Välj vilka leveransmetoder som ska användas och i vilken ordning.
          </Typography>
        </Box>
        {/* Prioritetsväljaren är delad med beställningen av påminnelser (deliveryMethodPicker). */}
        <DeliveryMethodPicker
          value={orderString}
          onChange={(next) => setValue('deliveryMethodOrder', next, { shouldValidate: true })}
          coverage={hideCounts ? null : coverage}
          channelsLoading={!hideCounts && channelsLoading}
        />

        {/* Kivra valt men inte aktiverat: OK att boka, men gul varning om att utskicket inte genomförs förrän aktivering är klar. */}
        {order.includes('kivra') && kivraConfigured === false && (
          <Box sx={{ mt: 2, pl: { xs: 6, sm: 7 } }}>
            <Alert severity='warning'>
              Kivra är valt som leveransmetod men <b>inte aktiverat</b> för faktureringsgruppen. Du kan boka utskicket, men det genomförs inte förrän
              Kivra-aktiveringen är slutförd.{' '}
              <Link href={KIVRA_HELP_URL} target='_blank' rel='noopener' sx={{ fontWeight: 600 }}>
                Så aktiverar du Kivra
              </Link>
            </Alert>
          </Box>
        )}
      </Grid>

      {/* Antal som skickas – progressbar (höger). För en påminnelse vars föregående utskick inte förfallit går
          antalet inte att räkna; då förklaras det här i stället. */}
      <Grid item xs={12} md={5}>
        <Box sx={{ borderLeft: '1px solid', borderColor: 'divider', p: 5, height: '100%' }}>
          <Typography variant='subtitle1' sx={{ fontWeight: 700, mb: 2 }}>
            Antal som skickas
          </Typography>

          {hideCounts && (
            <Typography variant='body2' color='text.secondary'>
              Antalet går inte att räkna än. Påminnelsen går till dem som inte har betalat
              {reminderPreviousPayDate ? ` – fakturorna förfaller ${reminderPreviousPayDate.format('D MMM YYYY')}` : ''}, och antalet fastställs när
              påminnelsen produceras.
            </Typography>
          )}

          {!hideCounts && coverage && channelsLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: 'text.secondary', py: 1 }}>
              <CircularProgress size={18} />
              <Typography variant='body2'>Beräknar hur många som kan nås …</Typography>
            </Box>
          )}

          {!hideCounts && coverage && !channelsLoading && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}>
                <Typography variant='h4' sx={{ fontWeight: 700, lineHeight: 1 }}>
                  {pre}
                  {reachedTotal}
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  av {coverage.total} mottagare
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', height: 14, borderRadius: 7, overflow: 'hidden', bgcolor: 'action.disabledBackground', mb: 1.5 }}>
                {segments.map((s) => (
                  <Box
                    key={s.key}
                    sx={{
                      width: `${coverage.total > 0 ? (s.inc / coverage.total) * 100 : 0}%`,
                      bgcolor: CHANNEL_COLOR[s.key],
                      transition: 'width .4s ease',
                    }}
                  />
                ))}
              </Box>

              {segments.map((s) => (
                <Box key={s.key} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: CHANNEL_COLOR[s.key], flexShrink: 0 }} />
                  <Typography variant='body2' sx={{ flex: 1, color: 'text.secondary' }}>
                    {labelOf(s.key)}
                  </Typography>
                  <Typography variant='body2' sx={{ fontWeight: 700 }}>
                    {pre}
                    {s.inc}
                  </Typography>
                </Box>
              ))}

              {order.length === 0 && (
                <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
                  Välj minst en leveransmetod.
                </Typography>
              )}
              {order.length > 0 && notReached === 0 && (
                <Alert severity='success' icon={<ThumbUpAltOutlined fontSize='inherit' />} sx={{ mt: 1.5 }}>
                  Alla fakturor kan skickas ut
                </Alert>
              )}
              {finalized && notReached > 0 && (
                <Alert severity='error' sx={{ mt: 1.5 }}>
                  {pre}
                  {notReached} st kommer ej skickas
                </Alert>
              )}

              {/* Nedbrytning per land: hur många av mottagarna som har e-post / personnummer / postnummer. */}
              {breakdown && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ maxHeight: 300, overflowY: 'auto', pr: 0.5 }}>
                    {breakdown.countries.map((g) => (
                      <Box key={g.country} sx={{ mb: 2 }}>
                        <Typography variant='subtitle2' sx={{ fontWeight: 700, mb: 0.5 }}>
                          {g.country}{' '}
                          <Box component='span' sx={{ color: 'text.secondary', fontWeight: 500 }}>
                            ({pre}
                            {g.count})
                          </Box>
                        </Typography>
                        {[
                          { icon: <EmailOutlined sx={{ fontSize: 17, color: 'text.secondary' }} />, label: 'Har e-post', value: g.email },
                          { icon: <BadgeOutlined sx={{ fontSize: 17, color: 'text.secondary' }} />, label: 'Har personnummer', value: g.pnr },
                          {
                            icon: <MarkunreadMailboxOutlined sx={{ fontSize: 17, color: 'text.secondary' }} />,
                            label: 'Har postnummer',
                            value: g.post,
                          },
                        ].map((row) => (
                          <Box key={row.label} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            {row.icon}
                            <Typography variant='body2' sx={{ flex: 1, color: 'text.secondary' }}>
                              {row.label}
                            </Typography>
                            <Typography variant='body2' sx={{ fontWeight: 700 }}>
                              {pre}
                              {row.value}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    ))}
                  </Box>
                </>
              )}
            </>
          )}
          {!coverage && (
            <Typography variant='body2' color='text.secondary'>
              Gör ett urval i Medlemmar-steget för att se hur många som kan skickas.
            </Typography>
          )}
        </Box>
      </Grid>
    </GridWrapper>
  );
};
